/**
 * lostPlayKind — which half of the `lost_play` check fired, recovered from the
 * wiring snapshot the peer already carries.
 *
 * Read from the snapshot rather than threaded out of `classifyPublication`
 * because the snapshot is taken in the same synchronous tick as the verdict
 * and is already the evidence the report shows. Reading the element a second
 * time here would describe the same moment twice.
 */

import type { LostPlayKind } from './deadAirTypes'

/**
 * The kind for a `lost_play` peer, from `snapshotPeer`'s output. Undefined when
 * the snapshot holds no element, or the element's reads failed: an unknown
 * kind is reported as absent, never guessed.
 *
 * `elements[0]` because that is the element the classifier judged.
 */
export function lostPlayKindFromWiring(
  wiring: Record<string, unknown> | undefined
): LostPlayKind | undefined {
  const elements = wiring?.elements
  if (!Array.isArray(elements) || elements.length === 0) return undefined
  const el = elements[0] as Record<string, unknown> | null | undefined
  if (!el) return undefined
  if (el.paused === true) return 'paused'
  if (el.paused !== false) return undefined
  const readyState = el.readyState
  if (typeof readyState !== 'number') return undefined
  // HAVE_CURRENT_DATA (2) and below: no frame to play from.
  return readyState <= 2 ? 'not_ready' : 'frozen'
}
